import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import {
  Validators,
  FormArray,
  FormBuilder,
  FormGroup,
  FormControl,
  AbstractControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { CodeDTO } from 'src/app/shared/models/dtos/code-dto/code-dto';
import {
  asyncValidator,
  uniqueControlFormArrayValidator,
} from 'src/app/shared/validators/registration-validators';
import { DialogModalData } from '../../molecules/dialog-modal/dialog-modal.component';
import { ValidatePointCodeResponseDTO } from 'src/app/shared/models/dtos/responses/validate-point-code-response-dto/validate-point-code-response-dto';
import { FormControlStatus } from 'src/app/shared/models/constants/form-control-status';
import { CodeInputsSettings } from 'src/app/shared/models/constants/code-input-settings';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { ProductService } from 'src/app/core/api/product/product.service';

export type TPointCodeFormArray = FormArray<TPointCodeFormGroup>;

export type TPointCodeFormGroup = FormGroup<{
  equipment: FormControl<string | null>;
  code: FormControl<string | null>;
  pointCodeMessage: FormControl<string | null>;
  pointQuantity: FormControl<number | null>;
}>;

@Component({
  selector: 'app-points-rescue',
  templateUrl: './points-rescue.component.html',
  styleUrls: ['./points-rescue.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Display the points rescue content
 */
export class PointsRescueComponent implements OnInit, OnDestroy {
  @Input() options: string[] = [];
  subsink = new Subscription();
  isLoading: boolean = false;
  serverError: boolean = false;
  equipmentExistsError = false;
  labels = {
    title: 'add-points-title',
    description: 'add-points-description',
    addMoreCodes: 'add-more-codes',
    rescueCode: 'rescue-code',
    selectEquipment: 'select-your-equipment',
  };
  pointTotalLabel = '';
  pointQuantity = 0;
  private _rescueLoading = false;

  private errorDialogData: DialogModalData = {
    content: 'rescue-code-error',
    successButtonText: 'ok',
  };
  private mostRecentlyChosenEquipment = '';

  form!: TPointCodeFormArray;

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private readonly dialogService: DialogService,
    private readonly rewardService: RewardService,
    private readonly productService: ProductService,
    private readonly fb: FormBuilder,
    private readonly topHeaderService: TopHeaderService
  ) {
    this.initFormGroup = this.initFormGroup.bind(this);
  }

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.translationService.translateObject(this.errorDialogData);
    this.loadEquipments();
    this.form = this.initForm();
  }

  /**
   * Load equipments serials from user's business
   */
  async loadEquipments() {
    const businessEquipments = await firstValueFrom(
      this.productService.getBusinessEquipments()
    );
    this.options = businessEquipments.map((equipment) => equipment.serial);
  }

  /**
   * on destroy
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * if rescue is loading
   */
  get rescueLoading() {
    return this._rescueLoading;
  }

  /**
   * Dynamically creating the form
   * @param {TPointCodeFormArray} pointCodeFormArray
   * @return {TPointCodeFormGroup}
   */
  private createTuple(
    pointCodeFormArray: TPointCodeFormArray
  ): TPointCodeFormGroup {
    const pointCodeFormGroup = this.fb.group({
      equipment: [this.mostRecentlyChosenEquipment, Validators.required],
      code: [
        '',
        [
          Validators.required,
          uniqueControlFormArrayValidator(
            pointCodeFormArray,
            (control: AbstractControl) => control.get('code')?.value
          ),
        ],
      ],
      pointCodeMessage: '',
      pointQuantity: NaN,
    });

    const equipmentControl = pointCodeFormGroup.controls['equipment'];

    this.subsink.add(
      equipmentControl.valueChanges.subscribe((equipment) => {
        equipment && (this.mostRecentlyChosenEquipment = equipment);
      })
    );

    const codeControl = pointCodeFormGroup.controls['code'];

    this.subsink.add(
      codeControl.valueChanges.subscribe(() => {
        pointCodeFormGroup.controls['pointCodeMessage'].setValue('');
        pointCodeFormGroup.controls['pointQuantity'].setValue(NaN);
      })
    );

    let oldStatus = codeControl.status;
    this.subsink.add(
      codeControl.statusChanges.subscribe((status) => {
        if (
          status === FormControlStatus.VALID ||
          oldStatus === FormControlStatus.VALID
        ) {
          oldStatus = status;
          this.updatePointQuantity();
        }
      })
    );

    pointCodeFormGroup.controls['code'].addAsyncValidators(
      asyncValidator(
        this.validationFunction(pointCodeFormGroup),
        'invalid-code'
      )
    );
    pointCodeFormGroup.updateValueAndValidity();

    return pointCodeFormGroup;
  }

  /**
   * Creates a blank TPointCodeFormArray
   * @return {TPointCodeFormArray}
   */
  private initForm(): TPointCodeFormArray {
    const pointCodeFormArray = this.fb.array<TPointCodeFormGroup>([]);
    pointCodeFormArray.push(this.createTuple(pointCodeFormArray));
    return pointCodeFormArray;
  }

  /**
   * Rescues inserted codes
   */
  async rescue() {
    this._rescueLoading = true;
    try {
      const codes = this.form.value.filter(
        (formGroup) => Boolean(formGroup.code) && Boolean(formGroup.equipment)
      );
      await firstValueFrom(
        this.rewardService.addPoints(
          codes.map(
            (pointCodeFormGroup) =>
              new CodeDTO(
                pointCodeFormGroup.equipment!,
                pointCodeFormGroup.code!
              )
          )
        )
      );

      this.topHeaderService.fetchTopHeaderInfo();

      const confirmationDialogData = this.pointRescueConfirmationDialogData;
      confirmationDialogData.content =
        await this.translationService.translatePlural(
          confirmationDialogData.content,
          this.pointQuantity
        );
      await this.translationService.translateObject(confirmationDialogData);
      this.dialogService.openDialog(confirmationDialogData);
      this.initFormGroup();
    } catch (err) {
      this.dialogService.openDialog(this.errorDialogData);
    }
    this._rescueLoading = false;
  }

  /**
   * Adds new code
   */
  addCodes() {
    this.form.push(this.createTuple(this.form));
  }

  /**
   * returns a new blank code form control
   * Removes code
   * @param {number} index
   */
  deleteCode(index: number) {
    this.form.removeAt(index);
    this.updatePointQuantity();
  }

  /**
   * Verifies number of inputs
   * @return {boolean}
   */
  hasReachedMaxInput(): boolean {
    const numberOfTuples = this.form.controls.length;
    return numberOfTuples === CodeInputsSettings.MAX_CODE_INPUT;
  }

  /**
   * Getting the fields from the form group
   * @param {number} index
   * @return {FormGroup} index
   */
  getTuplesFormGroup(index: number): TPointCodeFormGroup {
    return this.form.controls[index];
  }

  /**
   * Gets a control from a tuple from a TPointCodeFormGroup
   * @param {number} index
   * @param {'equipment' | 'code' | 'pointCodeMessage'} controlName
   * @return {FormControl<string | null>}
   */
  getControlFromTuple(
    index: number,
    controlName: 'equipment' | 'code' | 'pointCodeMessage'
  ): FormControl<string | null> {
    return this.getTuplesFormGroup(index).controls[controlName];
  }

  /**
   * Enables ou disable close icon
   * @return {boolean}
   */
  hasCloseButton(): boolean {
    return this.form.length > 1;
  }

  /**
   * Gets the point code subscript, if any
   * @param {number} index which control from our array of controls
   * @return {string}
   */
  getPointsCodeSubscript(index: number): string {
    return this.getControlFromTuple(index, 'pointCodeMessage').value || '';
  }

  /**
   * Resets points rescue form group
   */
  private initFormGroup() {
    this.form = this.initForm();
    this.updatePointQuantity();
  }

  /**
   * Retrieves a dialog data for point rescue confirmation modal
   */
  private get pointRescueConfirmationDialogData() {
    return {
      content: 'rescue-code-success',
      successButtonText: 'ok',
    };
  }

  /**
   * Validates code
   * @param {TPointCodeFormGroup} pointCodeFormGroup
   * @return {Function}
   */
  private validationFunction(pointCodeFormGroup: TPointCodeFormGroup) {
    return async (code: string) => {
      let validationResponse: ValidatePointCodeResponseDTO;
      try {
        validationResponse = await firstValueFrom(
          this.rewardService.validatePointCode(code)
        );
      } catch (err) {
        return false;
      }

      const isValid = validationResponse.valid;

      if (isValid) {
        const points = validationResponse.pointQuantity;

        pointCodeFormGroup.controls['pointQuantity'].setValue(points);

        this.translationService
          .translatePlural('points-code', points)
          .then((pointCodeMessage) => {
            pointCodeFormGroup.controls['pointCodeMessage'].setValue(
              pointCodeMessage
            );
          });
      }
      return isValid;
    };
  }

  /**
   * Updates point quantity
   */
  private updatePointQuantity() {
    this.pointQuantity = this.form.controls.reduce(
      (accumulator, pointCodeFormGroup) =>
        accumulator + (pointCodeFormGroup.controls['pointQuantity'].value || 0),
      0
    );
    this.translationService
      .translatePlural('points-total', this.pointQuantity)
      .then((label) => {
        this.pointTotalLabel = label;
      });
  }
}
